import {
  LeadsApi,
  type Lead,
  type LeadListResponseDto,
  type LeadListScope,
  type LeadSource,
  type LeadStatus,
  type LeadQuerySortBy,
  type QuerySortDirection,
} from '@up2stay/up2promo-api-client';

import {
  LIST_FETCH_ALL_MAX_PAGES,
  LIST_PAGE_HARD_CAP,
} from '@/core/lib/list-pagination';

/** Matches backend `LIST_PAGE_HARD_CAP` / `clampListLimit`. */
export const LEADS_LIST_PAGE_LIMIT = LIST_PAGE_HARD_CAP;

export type FetchAllLeadsQuery = {
  includeArchived?: boolean;
  archivedOnly?: boolean;
  sortBy?: LeadQuerySortBy;
  sortOrder?: QuerySortDirection;
  scope?: LeadListScope;
  includeActivities?: boolean;
  profileId?: string;
  q?: string;
  status?: LeadStatus;
  tags?: string[];
  eventIds?: string[];
  source?: LeadSource;
  suggest?: boolean;
};

export type FetchAllLeadsResult = {
  items: Lead[];
  scopeCounts: LeadListResponseDto['scopeCounts'];
  suggestions?: LeadListResponseDto['suggestions'];
};

/**
 * Loads every matching lead by paging `LeadsApi.find` until a partial page.
 * Required because the API hard-caps `limit` at 100.
 */
export async function fetchAllLeadsPages(
  workspaceId: string,
  query: FetchAllLeadsQuery = {},
): Promise<Lead[]> {
  const result = await fetchAllLeadsWithMeta(workspaceId, query);
  return result.items;
}

/** Same as {@link fetchAllLeadsPages} but keeps scope counts from the first page. */
export async function fetchAllLeadsWithMeta(
  workspaceId: string,
  query: FetchAllLeadsQuery = {},
): Promise<FetchAllLeadsResult> {
  const byId = new Map<string, Lead>();
  let scopeCounts: LeadListResponseDto['scopeCounts'] = {
    mine: 0,
    workspace: 0,
    othersPrivate: 0,
    archivedHidden: 0,
  };
  let suggestions: LeadListResponseDto['suggestions'];

  for (let page = 0; page < LIST_FETCH_ALL_MAX_PAGES; page += 1) {
    const skip = page * LEADS_LIST_PAGE_LIMIT;
    const response = await LeadsApi.find({
      path: { workspaceId },
      query: {
        ...query,
        limit: LEADS_LIST_PAGE_LIMIT,
        skip,
      },
    });

    const payload = response.data;
    const batch = payload?.items ?? [];
    if (page === 0 && payload?.scopeCounts) {
      scopeCounts = payload.scopeCounts;
      suggestions = payload.suggestions;
    }
    for (const lead of batch) {
      if (lead.id) byId.set(lead.id, lead);
    }
    if (batch.length === 0 || batch.length < LEADS_LIST_PAGE_LIMIT) {
      break;
    }
  }

  return {
    items: [...byId.values()],
    scopeCounts,
    suggestions,
  };
}
